import {
  CATEGORY_CONFIG,
  isLeaderboardCategory,
  LeaderboardCategory,
  LeaderboardEntry,
  LeaderboardRecordCategory,
  LeaderboardStandingBoard,
} from 'types/interfaces/leaderboard';

/** Boards whose values are points: they render through the Points component. */
export const POINTS_CATEGORIES: readonly LeaderboardCategory[] = ['most_points', 'points_sent', 'biggest_trade'];

export const isPointsCategory = (category: LeaderboardCategory): boolean =>
  POINTS_CATEGORIES.includes(category);

/** `?tab=` is a board; anything unknown falls back to the default board. */
export const parseTabParam = (value: string | null | undefined): LeaderboardCategory =>
  isLeaderboardCategory(value) ? value : 'most_trades';

/** "Oct 2026" */
export const formatMonthYear = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
};

/** A board value as text. Points boards render through Points instead. */
export const formatDisplayValue = (category: LeaderboardCategory, value: number): string => {
  if (category === 'highest_rated') return value.toFixed(1);
  if (category === 'rising_stars') return `+${value}%`;
  return value.toLocaleString();
};

/** The line under a username in the table (Decision 5). */
export const entrySubLine = (entry: LeaderboardEntry, category: LeaderboardCategory): string => {
  if (category === 'highest_rated') {
    const n = entry.reviewCount ?? 0;
    return `${n.toLocaleString()} review${n === 1 ? '' : 's'}`;
  }
  if (category === 'biggest_trade' && entry.trade) {
    return `${entry.trade.cardsA} ⇄ ${entry.trade.cardsB} cards · ${formatMonthYear(entry.trade.completedAt)}`;
  }
  if (category === 'new_members') {
    const n = entry.wantListCount ?? 0;
    return `${n.toLocaleString()} want${n === 1 ? '' : 's'}`;
  }
  const joined = `joined ${formatMonthYear(entry.memberSince)}`;
  // A missing count is not zero: show the join month alone rather than a
  // false "0 trades".
  if (entry.totalTrades == null) return joined;
  const t = entry.totalTrades;
  return `${t.toLocaleString()} trade${t === 1 ? '' : 's'} · ${joined}`;
};

/**
 * Standing bar width in percent (Decision 11): 100 at rank 1, 0 when unranked,
 * otherwise the viewer's value as a share of the value one rank above.
 */
export const standingBarWidth = (board: LeaderboardStandingBoard): number => {
  if (board.rank === null) return 0;
  if (board.rank === 1) return 100;
  if (!board.above || board.above.displayValue <= 0) return 100;
  return Math.max(0, Math.min(100, Math.round((board.displayValue / board.above.displayValue) * 100)));
};

/** Gap to the rank above: whole numbers, except highest_rated (2 decimals). */
export const formatGap = (category: LeaderboardCategory, gap: number): string =>
  category === 'highest_rated' ? Math.max(0, gap).toFixed(2) : Math.max(0, Math.round(gap)).toLocaleString();

/**
 * The gap line under a standing row (Decision 12), split so a points-valued gap
 * can render through the Points component:
 *   - text     the whole line when there is no gap number
 *   - gap      the number (points boards: render <Points points={gapPoints}/>)
 *   - before / after  copy around the number
 */
export interface GapLine {
  text?: string;
  before?: string;
  gap?: string;
  gapPoints?: number;
  after?: string;
}

export const standingGapLine = (board: LeaderboardStandingBoard): GapLine => {
  const { category } = board;
  if (category === 'new_members') return { text: `Joined ${formatMonthYear(board.memberSince)}` };
  if (board.rank === null) return { text: 'Not ranked yet' };
  if (board.rank === 1 || !board.above) return { text: "You're #1" };

  const rawGap = board.above.displayValue - board.displayValue;
  const gap = formatGap(category, rawGap);
  const above = board.rank - 1;
  const points = isPointsCategory(category);

  if (category === 'biggest_trade') {
    return {
      before: 'Your best trade is ',
      gap: `${gap} points`,
      gapPoints: points ? Math.max(0, Math.round(rawGap)) : undefined,
      after: ` short of #${above}`,
    };
  }
  const unit = points ? '' : ` ${CATEGORY_CONFIG[category].statLabel}`;
  return {
    gap: `${gap}${unit}`,
    gapPoints: points ? Math.max(0, Math.round(rawGap)) : undefined,
    after: ` behind ${board.above.username} (#${above})`,
  };
};

/** Records card rows, in display order (Decision 14). */
export const RECORD_ROWS: ReadonlyArray<{ category: LeaderboardRecordCategory; label: string }> = [
  { category: 'biggest_trade', label: 'Biggest single trade' },
  { category: 'longest_streak', label: 'Longest trade streak' },
  { category: 'trade_partners', label: 'Most trade partners' },
  { category: 'points_sent', label: 'Most points sent' },
];

/** "A & B" for a trade entry, the username otherwise. */
export const entryHolder = (entry: LeaderboardEntry): string =>
  entry.trade ? `${entry.username} & ${entry.trade.partner.username}` : entry.username;
